/**
 * The theme's brand colours as CSS custom properties - the ONE mapping every
 * surface builds from - and the rule that picks a readable ink for each colour.
 *
 * Several places paint a site theme, and they need the same names with the same
 * values: the published site's page (`themeColorCss`), an editing surface's inline
 * style (`themeSurfaceVars`: the builder canvas and the admin previews that carry
 * the site theme), the admin/account shell root (`applyThemeColorVars`) and the
 * designs gallery cards (`showcaseThemeStyle` in @wa/widgets). Each used to carry
 * its own field list, and the lists drifted: the live site wrote no foregrounds,
 * the canvas no `--color-secondary`, the admin kept a private copy of the ink rule.
 * Now each one starts from `themeBrandVars` (+ `gradientVars`) and only adds what
 * is really its own (page background, fonts).
 *
 * No Google Fonts catalogue behind this file (unlike `theme.ts`), so a render
 * bundle can import it without paying for the font list.
 */
import type { SiteTheme } from "./theme.js";

/** Dark ink: the @wa/ui foreground. */
export const DARK_INK = "oklch(0.18 0.015 270)";
/** Light ink: white. */
export const LIGHT_INK = "oklch(1 0 0)";
/** WCAG relative luminance of `DARK_INK` (oklch -> linear sRGB -> Y). */
export const DARK_INK_LUM = 0.0058;

/** sRGB channel (0-1) -> linear light. */
const toLinear = (c: number): number => (c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));

/** A 6-digit hex colour (`#` optional) as sRGB channels 0-1, or null. */
function hexRgb(hex: string): [number, number, number] | null {
  const m = hex.replace("#", "").match(/^([0-9a-f]{6})$/i);
  if (!m) return null;
  const hh = m[1]!;
  return [0, 2, 4].map((i) => parseInt(hh.slice(i, i + 2), 16) / 255) as [number, number, number];
}

/** Relative luminance of sRGB channels 0-1. */
const rgbLuminance = ([r, g, b]: [number, number, number]): number =>
  0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);

/** WCAG relative luminance of a 6-digit hex colour (`#` optional), or null for anything else. */
export function hexLuminance(hex: string): number | null {
  const rgb = hexRgb(hex);
  return rgb ? rgbLuminance(rgb) : null;
}

/**
 * Relative luminance of `oklch(L C H)` (L as a number or a percentage), clipped to
 * sRGB, or null for anything else. The gradient's "fade" end is written this way.
 */
function oklchLuminance(color: string): number | null {
  const m = color.trim().match(/^oklch\(\s*([\d.]+)(%?)\s+([\d.]+)\s+([\d.]+)(?:deg)?\s*\)$/i);
  if (!m) return null;
  const L = Number(m[1]) / (m[2] ? 100 : 1);
  const C = Number(m[3]);
  const h = (Number(m[4]) * Math.PI) / 180;
  const a = C * Math.cos(h), b = C * Math.sin(h);
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const mm = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3;
  const clip = (v: number) => Math.min(1, Math.max(0, v));
  const r = clip(4.0767416621 * l - 3.3077115913 * mm + 0.2309699292 * s);
  const g = clip(-1.2684380046 * l + 2.6097574011 * mm - 0.3413193965 * s);
  const bl = clip(-0.0041960863 * l - 0.7034186147 * mm + 1.707614701 * s);
  return 0.2126 * r + 0.7152 * g + 0.0722 * bl;
}

/** WCAG contrast ratio of two relative luminances. */
const contrast = (a: number, b: number): number => (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);

/**
 * The lowest WCAG contrast at which white ink is still used: 3:1, the WCAG 2 level
 * for large text and for the parts of a control. Above it white wins even where dark
 * ink has the higher WCAG number.
 */
export const WHITE_INK_FLOOR = 3;

/**
 * Does a colour of this luminance take dark ink? Only when white falls under
 * `WHITE_INK_FLOOR`; below that line dark ink always has the higher contrast too.
 *
 * Why not simply the higher WCAG contrast: on saturated mid-tones the two inks score
 * almost the same (violet #8b5cf6: dark 4.44, white 4.23; blue #3b82f6: 5.12 and
 * 3.68), the WCAG formula then picks dark ink, and people read black text on a
 * violet or blue button as a mistake. The perceptual contrast of the WCAG 3 draft
 * (APCA) agrees with them: white reads clearly better on every colour down to about
 * 3:1 (Lc 74 against 32 on #8b5cf6). The floor keeps the fix for mid-bright colours,
 * where white fails badly: 2.28:1 on #22c55e, 2.14:1 on #38bdf8, 2.80:1 on #f97316
 * (dark ink gives 8.3, 8.8 and 6.7). The crossover sits near luminance 0.30.
 */
export function prefersDarkInkAt(lum: number): boolean {
  return contrast(lum, 1) < WHITE_INK_FLOOR;
}

/**
 * Readable text colour on a hex background (`DARK_INK` or `LIGHT_INK`), so button
 * and badge text stays legible. `null` if not a 6-digit hex.
 */
export function foregroundFor(hex: string): string | null {
  const lum = hexLuminance(hex);
  if (lum === null) return null;
  return prefersDarkInkAt(lum) ? DARK_INK : LIGHT_INK;
}

/**
 * Whether a hex colour is dark enough to carry white ink (null = not a 6-digit hex).
 *
 * The same answer `foregroundFor` gives, as a plain yes or no, for the places that
 * need the fact itself rather than an ink colour: a surface painted in the brand
 * colour has to know which of a logo's two files suits it.
 */
export function isDarkColor(hex: string): boolean | null {
  const lum = hexLuminance(hex);
  return lum === null ? null : !prefersDarkInkAt(lum);
}

/**
 * The ink for text laid over a gradient from `from` to `to`: white while its WORST
 * contrast along the fill keeps `WHITE_INK_FLOOR` (the solid rule, run on every
 * step), otherwise whichever of the two inks has the better worst contrast, so no
 * part of the fill loses the text. Between two hex ends the sRGB steps are checked too (the middle of a
 * red-to-green run is darker than both ends). `null` when an end is neither a hex
 * nor an `oklch()` colour.
 */
export function gradientForeground(from: string, to: string): string | null {
  const a = hexRgb(from), b = hexRgb(to);
  let lums: number[];
  if (a && b) {
    lums = [0, 0.25, 0.5, 0.75, 1].map((t) => rgbLuminance(a.map((c, i) => c + (b[i]! - c) * t) as [number, number, number]));
  } else {
    const la = a ? rgbLuminance(a) : oklchLuminance(from);
    const lb = b ? rgbLuminance(b) : oklchLuminance(to);
    if (la === null || lb === null) return null;
    lums = [la, lb];
  }
  const worst = (inkLum: number) => Math.min(...lums.map((l) => contrast(l, inkLum)));
  if (worst(1) >= WHITE_INK_FLOOR) return LIGHT_INK;
  return worst(DARK_INK_LUM) >= worst(1) ? DARK_INK : LIGHT_INK;
}

/** Hue of a hex colour in OKLCH (degrees), or null for a grey or a non-hex value. */
function hexToOklchHue(hex: string): number | null {
  const rgb = hexRgb(hex);
  if (!rgb) return null;
  const [lr, lg, lb] = rgb.map(toLinear) as [number, number, number];
  const l_ = Math.cbrt(0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb);
  const m_ = Math.cbrt(0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb);
  const s_ = Math.cbrt(0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb);
  const A = 1.9779984951 * l_ - 2.4285922050 * m_ + 0.4505937099 * s_;
  const B = 0.0259040371 * l_ + 0.7827717662 * m_ - 0.8086757660 * s_;
  if (Math.sqrt(A * A + B * B) < 0.01) return null;
  let H = (Math.atan2(B, A) * 180) / Math.PI;
  if (H < 0) H += 360;
  return H;
}

/**
 * The gradient CSS variables (`--color-gradient-from/to`, `--gradient-angle`), only
 * when the theme turns the gradient on. A blank secondary counts as unset, the same
 * as in `themeBrandVars`, so no surface ever writes an empty gradient end.
 */
export function gradientVars(theme: SiteTheme | null | undefined): Record<string, string> {
  const g = theme?.gradient;
  const primary = theme?.primary;
  if (!g?.enabled || !primary) return {};
  const mode = g.mode ?? "brand";
  const angle = g.angle ?? 135;
  let from = primary;
  let to = primary;
  if (mode === "brand") {
    to = theme?.secondary || primary;
  } else if (mode === "fade") {
    const h = hexToOklchHue(primary);
    if (h !== null) to = `oklch(0.97 0.02 ${h.toFixed(2)})`;
  } else if (mode === "custom") {
    from = g.customFrom || primary;
    to = g.customTo || theme?.secondary || primary;
  }
  return {
    "--color-gradient-from": from,
    "--color-gradient-to": to,
    "--gradient-angle": `${angle}deg`,
  };
}

/**
 * What the variables are for. `content` = anything that shows the SITE (the
 * published page, the builder canvas, the admin previews, the designs gallery).
 * `chrome` = an app shell that only borrows the brand (the admin and account UI,
 * the account-free template editor around its canvas).
 *
 * The one difference: `chrome` leaves `--color-secondary` alone. On site content the
 * theme's second colour is the kit's secondary fill (a secondary Button or Badge; the
 * section palette maps it the same way). In app chrome that token is the neutral
 * surface of every secondary control, and a tenant colour there would repaint the
 * whole backend. The live-edit panel puts the neutral value back for the same reason
 * (`apps/web_site/src/adminbar/live/liveCss.ts`).
 */
export type ThemeVarSurface = "content" | "chrome";

/**
 * Brand colours, their readable inks and the corner radius as custom properties.
 * Only the keys the theme actually defines, so an unset theme leaves every @wa/ui
 * default in place.
 */
export function themeBrandVars(
  theme: SiteTheme | null | undefined,
  surface: ThemeVarSurface = "content",
): Record<string, string> {
  const out: Record<string, string> = {};
  if (!theme) return out;
  const set = (name: string, value: string | null | undefined) => {
    if (value) out[name] = value;
  };
  const { primary, secondary, accent } = theme;
  // Second brand colour (aurora mid-stop, gradients, glows). Falls back to primary so
  // a single-colour theme does not leak the @wa/ui default violet.
  const primary2 = secondary || primary;

  if (primary) {
    set("--color-primary", primary);
    // For a SOLID primary fill (`bg-primary`, the widget buttons).
    set("--color-primary-foreground", foregroundFor(primary));
    // For text on `bg-brand-gradient` (the kit's primary Button, the chat launcher).
    // That fill runs from the primary to the second colour (or the theme gradient),
    // so a light primary with a near-black secondary needs an ink that survives
    // both ends, not the one picked for the primary alone.
    const grad = gradientVars(theme);
    const gradInk =
      gradientForeground(grad["--color-gradient-from"] ?? primary, grad["--color-gradient-to"] ?? primary2!) ??
      foregroundFor(primary);
    set("--color-brand-gradient-foreground", gradInk);
    // For text on a fill painted straight from the brand pair (`var(--color-primary)`
    // -> `var(--color-primary-2)`, the hero and call-to-action banners). Not the same
    // fill as above once the theme gradient is on: "fade" runs the primary into a
    // near-white tint and "custom" runs between two colours of the author's own, so
    // an ink worked out for that gradient can be dark on a navy brand pair.
    set("--color-primary-pair-foreground", gradientForeground(primary, primary2!) ?? foregroundFor(primary));
    // Focus ring (@wa/ui Input/Button `ring-ring`) follows the brand; without it the
    // @wa/ui default indigo shows through on every focused field.
    set("--color-ring", primary);
    // 1 when the brand colour itself is dark. A surface painted with it (the header's
    // full-screen menu) reads this to pick the logo file made for a dark ground.
    const dark = isDarkColor(primary);
    if (dark !== null) out["--color-primary-dark"] = dark ? "1" : "0";
  }
  set("--color-primary-2", primary2);
  if (surface === "content" && secondary) {
    set("--color-secondary", secondary);
    set("--color-secondary-foreground", foregroundFor(secondary));
  }
  if (accent) {
    set("--color-accent-brand", accent);
    set("--color-accent-brand-foreground", foregroundFor(accent));
  }
  if (typeof theme.radius === "number") out["--radius-base"] = `${theme.radius}px`;
  return out;
}

/**
 * The theme as dark mode paints it: `darkPrimary` and `darkSecondary` in place of
 * `primary` and `secondary`, or null when the theme has neither (the colours are the
 * same in both modes). A brand or fade gradient derives its ends from these, so it
 * follows; a custom gradient names its own ends and keeps them.
 */
export function darkModeTheme(theme: SiteTheme | null | undefined): SiteTheme | null {
  if (!theme) return null;
  const primary = theme.darkPrimary?.trim();
  const secondary = theme.darkSecondary?.trim();
  if (!primary && !secondary) return null;
  return { ...theme, primary: primary || theme.primary, secondary: secondary || theme.secondary };
}

/** The entries of `next` whose value differs from `base`. */
function changed(base: Record<string, string>, next: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(next)) if (base[k] !== v) out[k] = v;
  return out;
}

/**
 * The brand variables dark mode overrides (`themeBrandVars` of `darkModeTheme`),
 * only the ones that differ from light mode, inks included: a copper that takes dark
 * ink on white may be a copper that takes white ink in dark mode. Empty for a theme
 * without dark-mode colours.
 */
export function darkModeVars(theme: SiteTheme | null | undefined, surface: ThemeVarSurface = "content"): Record<string, string> {
  const dark = darkModeTheme(theme);
  return dark ? changed(themeBrandVars(theme, surface), themeBrandVars(dark, surface)) : {};
}

/** The gradient variables dark mode overrides, the same way as `darkModeVars`. */
export function darkModeGradientVars(theme: SiteTheme | null | undefined): Record<string, string> {
  const dark = darkModeTheme(theme);
  return dark ? changed(gradientVars(theme), gradientVars(dark)) : {};
}
